import { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
// import {
//   Image as ImageIcon, Download, Calendar,
//   User as UserIcon, ChevronLeft, ChevronRight, Folder
// } from "lucide-react";

import {
  Image as ImageIcon,
  Eye,
  Calendar,
  Check,
  ChevronDown,
  Search,
  User as UserIcon,
  ChevronLeft,
  ChevronRight,
  Folder
} from "lucide-react";




import { getEmployeesApi } from "../../app/api";
import { API_BASE_URL, FILE_BASE_URL } from "../../app/apiConfig";

const USERS_PER_FOLDER_PAGE = 5;
const SCREENSHOTS_PER_PAGE = 8;

const formatScreenshotDateTime = (value) => {
  if (!value) return "--";

  const rawValue = String(value).trim();
  const dateMatch = rawValue.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );

  if (dateMatch) {
    const [, year, month, day, hour = "00", minute = "00"] = dateMatch;
    return `${year}-${month}-${day} ${hour}:${minute}`;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "--";

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(parsedDate);
  const getPart = (type) => parts.find((part) => part.type === type)?.value || "00";

  return `${getPart("year")}-${getPart("month")}-${getPart("day")} ${getPart("hour")}:${getPart("minute")}`;
};

export default function PublicScreenshots() {
  const [screenshots, setScreenshots] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState("all");
  const [repositoryMenuOpen, setRepositoryMenuOpen] = useState(false);
  const [repositorySearch, setRepositorySearch] = useState("");
  const repositoryMenuRef = useRef(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Screenshots pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Folder (user) pagination
  const [folderPage, setFolderPage] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scResponse, empResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/screenshots/public`),


        getEmployeesApi().catch(() => ({ success: false, data: [] }))
      ]);

      if (scResponse.data && scResponse.data.success) {
        setScreenshots(scResponse.data.data || []);
      } else {
        toast.error("Failed to load screenshots data");
      }

      if (empResponse && empResponse.success) {
        setEmployees(empResponse.data || []);
      } else if (Array.isArray(empResponse)) {
        setEmployees(empResponse);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error connecting to APIs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        repositoryMenuRef.current &&
        !repositoryMenuRef.current.contains(event.target)
      ) {
        setRepositoryMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Map userId to employee name
  const getUserName = (userId) => {
    const emp = employees.find(e => e.id === Number(userId));
    if (emp) return `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || `User #${userId}`;
    return `User #${userId}`;
  };

  // Reset screenshots page when filter changes
  useEffect(() => { setCurrentPage(1); }, [selectedUser]);

  const screenshotUserIds = [...new Set(screenshots.map(s => String(s.userId)))];
  const employeeUserIds = employees
    .map((employee) => employee.id || employee.user_id)
    .filter(Boolean)
    .map(String);
  const uniqueUserIds = [...new Set([...employeeUserIds, ...screenshotUserIds])];
  const screenshotCountByUser = screenshots.reduce((acc, screenshot) => {
    const key = String(screenshot.userId);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const repositoryOptions = [
    { value: "all", label: "All Users", meta: `${screenshots.length} screenshots` },
    ...uniqueUserIds.map((u) => ({
      value: String(u),
      label: getUserName(u),
      meta: `ID: ${u} • ${screenshotCountByUser[String(u)] || 0} screenshots`,
    })),
  ];
  const selectedRepository =
    repositoryOptions.find((option) => option.value === String(selectedUser)) ||
    repositoryOptions[0];
  const normalizedRepositorySearch = repositorySearch.trim().toLowerCase();
  const filteredRepositoryOptions = normalizedRepositorySearch
    ? repositoryOptions.filter((option) =>
        `${option.label} ${option.value} ${option.meta}`
          .toLowerCase()
          .includes(normalizedRepositorySearch)
      )
    : repositoryOptions;

  const filteredScreenshots = selectedUser === "all"
    ? screenshots
    : screenshots.filter(s => s.userId === Number(selectedUser));

  // Screenshots pagination
  const totalItems = filteredScreenshots.length;
  const totalPages = Math.ceil(totalItems / SCREENSHOTS_PER_PAGE);
  const indexOfLastItem = currentPage * SCREENSHOTS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - SCREENSHOTS_PER_PAGE;
  const currentScreenshots = filteredScreenshots.slice(indexOfFirstItem, indexOfLastItem);

  // Group by user for folder view
  const groupedByUser = screenshots.reduce((acc, curr) => {
    if (!acc[curr.userId]) acc[curr.userId] = [];
    acc[curr.userId].push(curr);
    return acc;
  }, {});

  const allFolderEntries = Object.entries(groupedByUser);
  const totalFolderPages = Math.ceil(allFolderEntries.length / USERS_PER_FOLDER_PAGE);
  const folderIndexStart = (folderPage - 1) * USERS_PER_FOLDER_PAGE;
  const currentFolderEntries = allFolderEntries.slice(folderIndexStart, folderIndexStart + USERS_PER_FOLDER_PAGE);

  // Dot pagination renderer (shared)
  const renderDotPagination = (current, total, onChange) => {
    const pages = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, current - 1);
      let end = Math.min(total - 1, current + 1);
      if (current <= 3) end = Math.min(4, total - 1);
      if (current >= total - 2) start = Math.max(total - 3, 2);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < total - 1) pages.push("...");
      pages.push(total);
    }

    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(Math.max(current - 1, 1))}
          disabled={current === 1}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#21124f] border border-purple-500/20 hover:bg-[#2c1868] text-white rounded-lg text-sm transition disabled:opacity-30 disabled:pointer-events-none"
        >
          <ChevronLeft size={14} /> Prev
        </button>

        {pages.map((page, i) =>
          page === "..." ? (
            <span key={`d-${i}`} className="px-1 text-purple-300 font-bold select-none">...</span>
          ) : (
            <button
              key={`p-${page}`}
              onClick={() => onChange(page)}
              className={`w-8 h-8 rounded-lg text-sm font-bold transition ${
                current === page
                  ? "bg-purple-600 text-white shadow shadow-purple-600/30"
                  : "bg-[#21124f] hover:bg-[#2c1868] border border-purple-500/20 text-purple-200"
              }`}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => onChange(Math.min(current + 1, total))}
          disabled={current === total}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#21124f] border border-purple-500/20 hover:bg-[#2c1868] text-white rounded-lg text-sm transition disabled:opacity-30 disabled:pointer-events-none"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    );
  };

  return (
    <div className="text-white min-h-screen md:mt-20">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4 bg-gradient-to-br from-[#140c2b] via-[#1f1147] to-[#0f0825] p-6 rounded-2xl border border-purple-500/30 shadow-2xl">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600">
            Public Screenshots
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Browse and monitor captured user sessions in structured repositories
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-purple-200 font-semibold whitespace-nowrap">Select Repository:</label>
          <div className="relative w-[260px]" ref={repositoryMenuRef}>
            <button
              type="button"
              onClick={() => setRepositoryMenuOpen((value) => !value)}
              className={`group flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left shadow-[0_16px_38px_rgba(0,0,0,0.28)] transition ${
                repositoryMenuOpen
                  ? "border-cyan-300/70 bg-[#26145d] ring-2 ring-cyan-300/20"
                  : "border-purple-300/25 bg-white/8 hover:border-cyan-300/45 hover:bg-white/12"
              }`}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-black text-white">
                  {selectedRepository?.label || "All Users"}
                </span>
                <span className="mt-0.5 block truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-100/55">
                  {selectedRepository?.meta || "Repository"}
                </span>
              </span>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-cyan-300/12 text-cyan-100 ring-1 ring-cyan-200/18">
                <ChevronDown
                  size={17}
                  className={`transition duration-200 ${repositoryMenuOpen ? "rotate-180" : ""}`}
                />
              </span>
            </button>

            {repositoryMenuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-full overflow-hidden rounded-2xl border border-white/16 bg-[#160d31]/95 p-2 shadow-[0_26px_70px_rgba(0,0,0,0.48)] backdrop-blur-2xl">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_0%,rgba(34,211,238,0.18),transparent_32%),radial-gradient(circle_at_95%_0%,rgba(168,85,247,0.18),transparent_35%)]" />
                <div className="relative mb-2 flex items-center gap-2 rounded-xl border border-cyan-200/18 bg-white/8 px-3 py-2 text-white shadow-inner">
                  <Search size={15} className="shrink-0 text-cyan-100/70" />
                  <input
                    type="search"
                    value={repositorySearch}
                    onChange={(event) => setRepositorySearch(event.target.value)}
                    placeholder="Search user or ID..."
                    className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-white placeholder:text-white/36 outline-none"
                    autoFocus
                  />
                </div>
                <div className="relative max-h-64 space-y-1 overflow-y-auto pr-1">
                  {filteredRepositoryOptions.length === 0 ? (
                    <div className="rounded-xl px-3 py-5 text-center text-sm font-semibold text-white/50">
                      No users found
                    </div>
                  ) : filteredRepositoryOptions.map((option) => {
                    const selected = String(selectedUser) === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setSelectedUser(option.value);
                          setRepositorySearch("");
                          setRepositoryMenuOpen(false);
                        }}
                        className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                          selected
                            ? "bg-cyan-300 text-[#120926] shadow-[0_10px_24px_rgba(34,211,238,0.22)]"
                            : "text-white/84 hover:bg-white/9 hover:text-white"
                        }`}
                      >
                        <span
                          className={`grid h-8 w-8 shrink-0 place-items-center rounded-xl text-xs font-black ${
                            selected
                              ? "bg-[#120926]/12 text-[#120926]"
                              : "bg-white/10 text-cyan-100"
                          }`}
                        >
                          {option.value === "all" ? <Folder size={15} /> : option.label.charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-black">{option.label}</span>
                          <span className={`block truncate text-[11px] font-bold ${selected ? "text-[#120926]/62" : "text-white/42"}`}>
                            {option.meta}
                          </span>
                        </span>
                        {selected && <Check size={17} strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center p-20 text-white/40">
          <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mb-4" />
          <p className="text-lg">Loading screenshots from system...</p>
        </div>
      ) : screenshots.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-20 text-white/40">
          <ImageIcon size={64} className="mb-4 opacity-50" />
          <p className="text-lg">No screenshots have been uploaded yet.</p>
        </div>
      ) : (
        <>
          {/* ── USER STORAGE REPOSITORIES ── */}
          <div className="mb-10 bg-[#150a33] p-6 rounded-2xl border border-purple-500/20 shadow-xl">

            {/* Folder header row */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
              <h2 className="text-xl font-bold text-purple-300 flex items-center gap-2">
                <Folder className="text-purple-400" size={22} /> User Storage Repositories
                <span className="text-xs text-purple-400/60 font-normal ml-1">
                  ({allFolderEntries.length} users)
                </span>
              </h2>
              {selectedUser !== "all" && (
                <button
                  onClick={() => { setSelectedUser("all"); setFolderPage(1); }}
                  className="px-4 py-1.5 rounded-lg border-2 border-dashed border-purple-500/40 bg-purple-950/20 hover:bg-purple-950/40 text-purple-300 hover:scale-[1.03] text-sm font-semibold transition-all duration-300"
                >
                  Show All Repositories
                </button>
              )}
            </div>

            {/* 5 folder cards per page */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-5">
              {currentFolderEntries.map(([userId, userScs]) => {
                const isSelected = selectedUser === userId;
                return (
                  <div
                    key={userId}
                    onClick={() => setSelectedUser(userId)}
                    className={`cursor-pointer p-4 rounded-xl border transition-all duration-300 flex flex-col items-center justify-center gap-2 hover:scale-[1.03] shadow-md ${
                      isSelected
                        ? "bg-[#381e7d] border-purple-400 ring-2 ring-purple-400/40"
                        : "bg-[#21124f] hover:bg-[#2b1766] border-purple-500/20"
                    }`}
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                      <UserIcon size={20} className="text-white" />
                    </div>
                    <span className="font-bold text-sm text-center text-white truncate max-w-full px-1">
                      {getUserName(userId)}
                    </span>
                    <span className="text-xs text-purple-300/80 font-medium">ID: {userId}</span>
                    <span className="text-xs px-2 py-0.5 bg-black/30 rounded-full text-purple-200 mt-1">
                      {userScs.length} files
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Folder pagination — only when > 5 users */}
            {totalFolderPages > 1 && (
              <div className="flex justify-center pt-2">
                {renderDotPagination(folderPage, totalFolderPages, setFolderPage)}
              </div>
            )}
          </div>

          {/* ── SCREENSHOTS GRID ── */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-3">
            <h2 className="text-xl font-bold text-purple-300 flex items-center gap-2">
              <span className="w-2 h-6 bg-purple-500 rounded-full" />
              {selectedUser === "all" ? "All Screenshots" : `Screenshots — ${getUserName(selectedUser)}`}
            </h2>
            <span className="text-xs text-white/50 bg-[#1c0f3c] border border-purple-500/10 px-3 py-1 rounded-full">
              Showing {Math.min(indexOfFirstItem + 1, totalItems)}–{Math.min(indexOfLastItem, totalItems)} of {totalItems} items
            </span>
          </div>

          {currentScreenshots.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-purple-300/30 bg-white/[0.04] p-10 text-center">
              <ImageIcon size={42} className="mx-auto mb-3 text-purple-200/50" />
              <p className="text-lg font-bold text-white">No screenshots yet</p>
              <p className="mt-2 text-sm text-white/50">
                {selectedUser === "all"
                  ? "No tracker screenshots are available right now."
                  : `${getUserName(selectedUser)} has not uploaded screenshots for today or yesterday yet.`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {currentScreenshots.map(sc => (
              <div
                key={sc.id}


  onClick={() =>
    setPreviewImage(`${FILE_BASE_URL}/${sc.imagePath}`)
  }
                className="relative group rounded-xl overflow-hidden shadow-xl border border-purple-500/10 bg-[#1a0f35] hover:shadow-purple-500/20 transition-all duration-300"
              >
                {/* <img
                  src={`${FILE_BASE_URL}/${sc.imagePath}`}
                  alt={`Screenshot ${sc.id}`}
                  loading="lazy"
                  className="w-full h-[220px] object-cover object-top group-hover:scale-105 transition duration-500"
                  onError={e => {
                    e.target.src = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=500&auto=format&fit=crop&q=60";
                  }}
                /> */}
<img
  src={`${FILE_BASE_URL}/${sc.imagePath}`}
  alt={`Screenshot ${sc.id}`}
  loading="lazy"
  // onClick={() =>
  //   setPreviewImage(`${FILE_BASE_URL}/${sc.imagePath}`)
  // }
  className="w-full h-[220px] object-cover object-top group-hover:scale-105 transition duration-500 cursor-pointer"
  onError={e => {
    e.target.src =
      "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=500&auto=format&fit=crop&q=60";
  }}
/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e0722] via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-white truncate max-w-[150px]">{getUserName(sc.userId)}</p>
                      <p className="text-xs text-purple-300 font-medium">User ID: {sc.userId}</p>
                      <p className="text-xs text-white/60 flex items-center gap-1 mt-1 font-mono">
                        <Calendar size={12} />
                        {formatScreenshotDateTime(sc.createdAt)}
                      </p>
                    </div>
                    <a
                      href={`${FILE_BASE_URL}/${sc.imagePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-purple-600/90 p-2.5 rounded-full hover:bg-purple-500 transition shadow-lg"
                      title="View Screenshot"                    >
                      {/* <Download size={14} className="text-white" /> */}
<Eye size={14} className="text-white" />
                    </a>
                  </div>
                </div>
              </div>
              ))}
            </div>
          )}

          {/* Screenshots pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-12 pb-10">
              {renderDotPagination(currentPage, totalPages, setCurrentPage)}
            </div>
          )}
        </>
      )}






















{previewImage && (
  <div
    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
    onClick={() => setPreviewImage(null)}
  >
    <div
      className="relative max-w-7xl max-h-[90vh]"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => setPreviewImage(null)}
        className="absolute -top-12 right-0 text-white text-3xl font-bold"
      >
        ×
      </button>

      <img
        src={previewImage}
        alt="Preview"
        className="max-w-full max-h-[90vh] rounded-xl shadow-2xl"
      />
    </div>
  </div>
)}
    </div>
  );
}
